import type { Status1 } from '../types/Status1';
import type { MilestoneMapInterval } from '../types/MilestoneMap1';
import { isCoarserInterval, isFinerInterval } from './zoomLevels';
import {
  doIntervalsOverlap,
  isPointInInterval,
  isIntervalAContainedByIntervalB,
} from './comparisonUtils';
import { getIntervalStart } from './intervalUtils';

/**
 * Determines if a status is an inchstone (smaller milestone)
 */
export function isInchstone(status: Status1): boolean {
  // TODO: Implement this correctly
  return true;
}

/**
 * Determines if a status is a milestone
 */
export function isMilestone(status: Status1): boolean {
  // TODO: Implement this correctly
  return true;
}

export function filterItemsForInterval<T extends { forIntervalEnding: string; deadline?: string }>(
  items: T[] | undefined,
  intervalStart: Date,
  intervalEnd: Date,
): T[] {
  if (!items) return [];

  return items.filter((item) => {
    // Always prefer deadline over forIntervalEnding
    const targetDate = item.deadline ? new Date(item.deadline) : new Date(item.forIntervalEnding);

    // Use consistent boundary handling: exclude start, include end
    return isPointInInterval(targetDate, { start: intervalStart, end: intervalEnd }, false, true);
  });
}

/**
 * Gets the latest status that falls within the given interval.
 * This is used when we need to find the most recent status within a specific time range.
 *
 * @param statuses Array of statuses to search through
 * @param interval The interval to find a status for
 * @returns The latest status within the interval, or undefined if none found
 */
export function getLatestStatusInInterval(
  statuses: Status1[],
  interval: { start: Date; end: Date },
): Status1 | undefined {
  // Find all statuses that fall within the interval
  const statusesInInterval = statuses.filter((s) => {
    const statusDate = new Date(s.forIntervalEnding);
    // Use consistent boundary handling: exclude start, include end
    return isPointInInterval(statusDate, interval, false, true);
  });

  // If we found any statuses, return the one with the latest end date
  if (statusesInInterval.length > 0) {
    return statusesInInterval.sort(
      (a, b) => new Date(b.forIntervalEnding).getTime() - new Date(a.forIntervalEnding).getTime(),
    )[0];
  }

  return undefined;
}

/**
 * Maps a status to an adjusted interval based on zoom level.
 * Returns the most appropriate status for the given interval.
 *
 * @param statuses Array of statuses to search through
 * @param adjustedInterval The interval to find a status for
 * @param originalInterval The original interval type of the map
 * @param zoomLevel The current zoom level
 * @returns The most appropriate status for this interval, or undefined if none found
 */
export function getStatusForAdjustedInterval(
  statuses: Status1[],
  adjustedInterval: { start: Date; end: Date },
  originalZoomLevel: MilestoneMapInterval,
  zoomLevel: MilestoneMapInterval,
): Status1 | undefined {
  // If zoom matches original interval, find the latest status within the interval
  if (originalZoomLevel === zoomLevel) {
    return getLatestStatusInInterval(statuses, adjustedInterval);
  }

  // For zooming out: find any status that is contained within the larger interval
  if (isCoarserInterval(zoomLevel, originalZoomLevel)) {
    // Get all statuses that are contained within the larger interval
    const containedStatuses = statuses.filter((status) => {
      const statusDate = new Date(status.forIntervalEnding);
      const statusStartDate = getIntervalStart(statusDate, originalZoomLevel);
      const statusInterval = {
        start: statusStartDate,
        end: statusDate,
      };
      // Use containment check instead of overlap
      return isIntervalAContainedByIntervalB(statusInterval, adjustedInterval);
    });

    // Return the most recent status if any found
    if (containedStatuses.length > 0) {
      return containedStatuses.sort(
        (a, b) => new Date(b.forIntervalEnding).getTime() - new Date(a.forIntervalEnding).getTime(),
      )[0];
    }
  }

  // For zooming in: inherit status from the parent interval
  if (isFinerInterval(zoomLevel, originalZoomLevel)) {
    // Pass originalZoomLevel as the parent interval type to ensure we get the full parent interval
    const childInterval = adjustedInterval;
    const parentZoomLevel = originalZoomLevel;
    return inheritStatusForChildFromParent(statuses, childInterval, parentZoomLevel);
  }

  return undefined;
}

/**
 * Determines the status that should be inherited by a child interval from its parent.
 *
 * For example: All the weeks in a quarter should be able to inherit status from
 * a quarterly map with a status for the entire quarter. A "parent" interval
 * in this example could be Q4. A "child" interval here could be the first week
 * of December.
 *
 * Visual examples:
 * ```
 * Example 1: Single overlapping status - returns that status
 * [start P]------------Parent Status----------------------------[end P]
 *                 [start C]------Child Interval------[end C]
 *                 [start I]--Inherited Status of P---[end I]
 *
 * Example 2: Multiple overlapping statuses - returns most recent
 * [start P1]----Status 1----[end P1]    [start P2]----Status 2----[end P2]
 *                 [start C]------Child Interval------[end C]
 *                 [start I]--Inherited Status of 2---[end I]
 *
 * Example 3: No overlapping statuses - returns undefined
 * [start P]------------Parent Status----------------------------[end P]
 *                                                                        [start C]------Child Interval------[end C]
 * ```
 *
 * @param parentStatuses - Array of parent statuses to check
 * @param childInterval - The child interval that needs a status
 * @param parentIntervalType - The interval type of the parent map (e.g., 'quarters', 'months', 'weeks')
 * @returns The most recent overlapping status or undefined if none found
 */
export function inheritStatusForChildFromParent(
  parentStatuses: Status1[],
  childInterval: { start: Date; end: Date },
  parentIntervalType: MilestoneMapInterval,
): Status1 | undefined {
  // For each parent status, create its interval and check for overlap
  const overlappingStatuses = parentStatuses.filter((status) => {
    // Get the end date of the parent status
    const statusEndDate = new Date(status.forIntervalEnding);

    // Calculate the start date of the parent status's interval using the parent's interval type
    // This ensures we get the full parent interval (e.g., entire quarter) regardless of zoom level
    const statusStartDate = getIntervalStart(statusEndDate, parentIntervalType);

    // Create the parent status's interval
    const parentInterval = {
      start: statusStartDate,
      end: statusEndDate,
    };

    // Check if the child interval overlaps with this parent status's interval
    return doIntervalsOverlap(childInterval, parentInterval);
  });

  // If we found any overlapping statuses, return the most recent one.
  // (See example 2 above for desired behavior with more than one overlapping status.)
  if (overlappingStatuses.length > 0) {
    return overlappingStatuses.sort(
      (a, b) => new Date(b.forIntervalEnding).getTime() - new Date(a.forIntervalEnding).getTime(),
    )[0];
  }

  return undefined;
}
